import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Product } from '../types';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { hasRealImage } from '../utils/images';

interface BrandDetail {
  slug: string;
  name: string;
  products: Product[];
}

const BrandPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [brand, setBrand] = useState<BrandDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [sort, setSort] = useState('rating');

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`/api/brands/${slug}`)
      .then(async (r) => {
        if (!active) return;
        if (!r.ok) { setNotFound(true); setLoading(false); return; }
        setBrand(await r.json());
        setLoading(false);
      })
      .catch(() => { if (active) { setNotFound(true); setLoading(false); } });
    return () => { active = false; };
  }, [slug]);

  if (loading) return <div className="container-custom py-28 flex justify-center"><LoadingSpinner /></div>;
  if (notFound || !brand) return (
    <div className="container-custom py-24 text-center">
      <h1 className="mb-4">Brand not found</h1>
      <p className="text-earth-600 mb-8">We couldn't find that brand.</p>
      <Link to="/brands" className="btn btn-primary">Browse all brands</Link>
    </div>
  );

  const sorted = [...brand.products].sort((a, b) => {
    const imgDiff = Number(hasRealImage(b)) - Number(hasRealImage(a));
    if (imgDiff !== 0) return imgDiff;
    if (sort === 'health') return b.healthScore - a.healthScore;
    if (sort === 'price-low') return a.price - b.price;
    if (sort === 'price-high') return b.price - a.price;
    return b.rating - a.rating;
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${brand.name} on GoodCart`,
    url: `https://goodcart.org/brands/${brand.slug}`,
    about: { '@type': 'Brand', name: brand.name },
    numberOfItems: sorted.length,
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={`${brand.name}: clean picks`}
        description={`Shop ${brand.name} on GoodCart: ${sorted.length} vetted products with clean ingredients and honest ratings.`}
        image="/og-image.png"
        url={`https://goodcart.org/brands/${brand.slug}`}
        type="website"
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <section className="bg-sage-700 text-cream-50">
        <div className="container-custom py-6 md:py-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-sage-200 mb-2">
              <Link to="/brands" className="hover:text-cream-50">All brands</Link>
              <span aria-hidden>/</span>
              <span className="text-cream-50 font-medium">{brand.name}</span>
            </nav>
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-display text-cream-50">{brand.name}</h1>
              <p className="text-sage-200 text-sm font-medium">{sorted.length} curated {sorted.length === 1 ? 'product' : 'products'}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container-custom py-8 md:py-10">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h2 className="text-earth-900 text-xl">Products</h2>
          <label className="flex items-center gap-2 text-sm text-earth-600">
            Sort by
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-earth-200 bg-white px-4 py-2 text-earth-800 focus:outline-none focus:ring-2 focus:ring-sage-500">
              <option value="rating">Highest rated</option>
              <option value="health">Health score</option>
              <option value="price-low">Price: low to high</option>
              <option value="price-high">Price: high to low</option>
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {sorted.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
        </div>
      </div>
    </div>
  );
};

export default BrandPage;
